import { CommonModule } from '@angular/common';
import type { Signal } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { TOfferKind } from '@recruitee/offers-types';
import { OfferKind } from '@recruitee/offers-types';
import type { Placement } from '@recruitee/placements-types';
import {
  AvatarComponent,
  HeadingComponent,
  OfferStatusIndicatorComponent,
  RemoveHTMLTagsPipe,
} from '@recruitee/user-interface';

@Component({
  selector: 'rt-candidate-offer-tooltip',
  imports: [
    CommonModule,
    HeadingComponent,
    OfferStatusIndicatorComponent,
    TranslatePipe,
    AvatarComponent,
    RemoveHTMLTagsPipe,
  ],
  templateUrl: './candidate-offer-tooltip.component.html',
  styleUrl: './candidate-offer-tooltip.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateOfferTooltipComponent {
  private readonly hasNewTalentPools = injectDevFlag(DevFlag.NewTalentPools);

  public readonly OfferKind: typeof OfferKind = OfferKind;
  public placement = input.required<Placement>();

  public offerData = computed(() =>
    this.placement().talentPoolId ? this.placement().talentPool! : this.placement().offer!,
  );

  public job = computed(() => this.placement().offer!);
  public talentPool = computed(() =>
    this.hasNewTalentPools ? this.placement().talentPool! : this.placement().offer,
  );

  public kind: Signal<TOfferKind> = computed(() => this.offerData().kind);

  public offersStatuses: { [key: string]: string } = {
    published: this.i18n.translate(`app.offers.status.published`),
    internal: this.i18n.translate(`app.offers.status.internal`),
    draft: this.i18n.translate(`app.offers.status.draft`),
    closed: this.i18n.translate(`app.offers.status.closed`),
    archived: this.i18n.translate(`app.offers.status.archived`),
  };

  public workModels: { [key: string]: string } = {
    onSite: this.i18n.translate('app.offer_form.work_model_section.on_site.title'),
    hybrid: this.i18n.translate('app.offer_form.work_model_section.hybrid.title'),
    remote: this.i18n.translate('app.offer_form.work_model_section.remote.title'),
  };

  public workModelCopy = computed(() => {
    const models = [
      ...(this.job().onSite ? [this.workModels['onSite']] : []),
      ...(this.job().remote ? [this.workModels['remote']] : []),
      ...(this.job().hybrid ? [this.workModels['hybrid']] : []),
    ];
    return models.join(', ');
  });

  constructor(private i18n: I18nService) {}
}
